import React, { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import {
  Upload, FileText, AlertCircle, CheckCircle, Loader2,
  ArrowRight, ChevronDown, ChevronUp, AlertTriangle, X
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const statusColors = {
  paid: 'bg-emerald-100 text-emerald-700',
  sent: 'bg-blue-100 text-blue-700',
  draft: 'bg-neutral-100 text-neutral-600',
  partially_paid: 'bg-amber-100 text-amber-700',
  voided: 'bg-neutral-200 text-neutral-500',
};

export default function ZohoInvoiceImport({ open, onClose, onImportComplete }) {
  const [step, setStep] = useState('upload');
  const [loading, setLoading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [showInvoices, setShowInvoices] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const reset = useCallback(() => {
    setStep('upload');
    setLoading(false);
    setCsvFile(null);
    setPreviewData(null);
    setImportResult(null);
    setShowInvoices(false);
    setShowErrors(false);
  }, []);

  const handleClose = () => { reset(); onClose(); };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) { toast.error('Please upload a CSV file'); return; }

    setCsvFile(file);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await adminApi.post('/api/admin/accounting/import/zoho?mode=dry_run', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreviewData(res.data);
      setStep('preview');
      toast.success(`Found ${res.data.summary?.will_import || 0} invoices ready to import`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse CSV');
      setCsvFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!csvFile) { toast.error('CSV file lost — please re-upload'); reset(); return; }

    setLoading(true);
    setStep('importing');
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const res = await adminApi.post('/api/admin/accounting/import/zoho?mode=execute', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      setStep('done');
      toast.success(`Successfully imported ${res.data.imported} invoices`);
      if (onImportComplete) onImportComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const summary = previewData?.summary || {};
  const statusBreakdown = previewData?.status_breakdown || {};
  const errors = previewData?.errors || [];
  const toImport = previewData?.to_import || [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="import-invoices-dialog" aria-describedby="import-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg" data-testid="import-dialog-title">
            <Upload className="w-5 h-5" />
            Import Invoices
            <span id="import-desc" className="sr-only">Upload a Zoho CSV to import invoices</span>
            <div className="flex items-center gap-1 ml-3">
              {['upload', 'preview', 'done'].map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`w-2 h-2 rounded-full transition-colors ${
                    step === s || step === 'importing' && s === 'preview' ? 'bg-teal-500' :
                    ['upload','preview','importing','done'].indexOf(step) > ['upload','preview','importing','done'].indexOf(s) ? 'bg-teal-300' : 'bg-neutral-200'
                  }`} />
                  {i < 2 && <div className="w-4 h-px bg-neutral-200" />}
                </React.Fragment>
              ))}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {/* UPLOAD STEP */}
          {step === 'upload' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-6" data-testid="upload-step">
              <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center">
                <FileText className="w-10 h-10 text-blue-500" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-base font-semibold text-[#1a1a2e]">Upload Invoice CSV</h3>
                <p className="text-sm text-neutral-500 max-w-md">
                  Upload your Zoho Books invoice export CSV. We&apos;ll parse all invoices, match clients, and show you a preview before importing.
                </p>
              </div>
              <label className="cursor-pointer" data-testid="upload-label">
                <input type="file" accept=".csv" className="hidden" onChange={handleFileUpload} disabled={loading} data-testid="csv-file-input" />
                <div className="px-6 py-3 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors flex items-center gap-2">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {loading ? 'Analyzing CSV...' : 'Choose CSV File'}
                </div>
              </label>
              <div className="text-xs text-neutral-400 bg-white/40 rounded-xl p-4 max-w-lg space-y-1">
                <p className="font-medium text-neutral-500">How to export from Zoho Books:</p>
                <p>1. Go to Sales &rarr; Invoices in Zoho Books</p>
                <p>2. Click the hamburger menu (top-right) &rarr; Export Invoices</p>
                <p>3. Select date range, choose CSV format, and download</p>
              </div>
            </div>
          )}

          {/* PREVIEW STEP */}
          {step === 'preview' && previewData && (
            <div className="space-y-5 py-4" data-testid="preview-step">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryCard label="To Import" value={summary.will_import || 0} icon={<ArrowRight className="w-4 h-4" />} color="teal" testId="summary-to-import" />
                <SummaryCard label="Already Exist" value={summary.already_exists || 0} icon={<CheckCircle className="w-4 h-4" />} color="gray" testId="summary-existing" />
                <SummaryCard label="New Clients" value={summary.new_clients || 0} icon={<FileText className="w-4 h-4" />} color="blue" testId="summary-new-clients" />
                <SummaryCard label="Total Value" value={formatCurrency(summary.total_amount)} icon={<FileText className="w-4 h-4" />} color="emerald" testId="summary-total-value" isText />
              </div>

              {/* Status Breakdown */}
              {Object.keys(statusBreakdown).length > 0 && (
                <div className="space-y-2" data-testid="status-breakdown">
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Status Breakdown</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(statusBreakdown).map(([status, count]) => (
                      <span key={status} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-neutral-100 text-neutral-600'}`}>
                        {status.replace('_', ' ')} <span className="font-bold">{count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Formula Warnings */}
              {errors.length > 0 && (
                <div className="space-y-2" data-testid="formula-warnings">
                  <button
                    onClick={() => setShowErrors(!showErrors)}
                    className="flex items-center gap-2 text-xs font-medium text-amber-600 hover:text-amber-700"
                    data-testid="toggle-warnings-btn"
                  >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {errors.length} formula warning{errors.length > 1 ? 's' : ''} (will still be imported)
                    {showErrors ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                  {showErrors && (
                    <div className="max-h-40 overflow-y-auto border border-amber-200 rounded bg-amber-50 divide-y divide-amber-100">
                      {errors.map((err, i) => (
                        <div key={i} className="px-3 py-2 text-xs">
                          <span className="font-mono font-medium text-amber-800">{err.invoice}</span>
                          <span className="text-amber-600 ml-2">{err.detail}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Invoice List Toggle */}
              <div className="space-y-2" data-testid="invoice-list-section">
                <button
                  onClick={() => setShowInvoices(!showInvoices)}
                  className="flex items-center gap-2 text-xs font-medium text-neutral-600 hover:text-[#1a1a2e]"
                  data-testid="toggle-invoice-list-btn"
                >
                  <FileText className="w-3.5 h-3.5" />
                  {showInvoices ? 'Hide' : 'Show'} invoice list ({toImport.length})
                  {showInvoices ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
                {showInvoices && toImport.length > 0 && (
                  <div className="max-h-64 overflow-y-auto border rounded-xl">
                    <table className="w-full text-xs">
                      <thead className="bg-white/40 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-neutral-500">Invoice #</th>
                          <th className="text-left px-3 py-2 font-medium text-neutral-500">Customer</th>
                          <th className="text-left px-3 py-2 font-medium text-neutral-500">Date</th>
                          <th className="text-right px-3 py-2 font-medium text-neutral-500">Total</th>
                          <th className="text-left px-3 py-2 font-medium text-neutral-500">Status</th>
                          <th className="text-center px-3 py-2 font-medium text-neutral-500">Items</th>
                          <th className="text-center px-3 py-2 font-medium text-neutral-500">Client</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {toImport.map((inv, i) => (
                          <tr key={i} className="hover:bg-white/60" data-testid={`preview-row-${inv.invoice_number}`}>
                            <td className="px-3 py-2 font-mono font-medium text-[#1a1a2e]">{inv.invoice_number}</td>
                            <td className="px-3 py-2 text-neutral-600 max-w-[160px] truncate">{inv.customer}</td>
                            <td className="px-3 py-2 text-neutral-500">{inv.date}</td>
                            <td className="px-3 py-2 text-right font-medium text-[#1a1a2e]">{formatCurrency(inv.total)}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${statusColors[inv.status?.split(' -> ')[1]] || 'bg-neutral-100 text-neutral-600'}`}>
                                {inv.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center text-neutral-500">{inv.items_count}</td>
                            <td className="px-3 py-2 text-center">
                              {inv.client_found
                                ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mx-auto" />
                                : <span className="text-blue-500 text-[10px] font-medium">NEW</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* IMPORTING STEP */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4" data-testid="importing-step">
              <Loader2 className="w-12 h-12 text-neutral-400 animate-spin" />
              <p className="text-sm text-neutral-600">Importing {summary.will_import || 0} invoices...</p>
              <p className="text-xs text-neutral-400">Creating clients and invoices. This may take a moment.</p>
            </div>
          )}

          {/* DONE STEP */}
          {step === 'done' && importResult && (
            <div className="space-y-5 py-6" data-testid="done-step">
              <div className="flex flex-col items-center space-y-3">
                <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                </div>
                <h3 className="text-lg font-semibold text-[#1a1a2e]">Import Complete</h3>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <SummaryCard label="Imported" value={importResult.imported || 0} color="emerald" testId="result-imported" />
                <SummaryCard label="New Clients" value={importResult.new_clients_created || 0} color="blue" testId="result-new-clients" />
                <SummaryCard label="Skipped (Existing)" value={importResult.already_exists_skipped || 0} color="gray" testId="result-skipped" />
                <SummaryCard label="Total Value" value={formatCurrency(importResult.summary?.total_amount)} color="teal" testId="result-total-value" isText />
              </div>

              {importResult.errors?.length > 0 && (
                <div className="border border-amber-200 rounded-xl bg-amber-50 p-3 space-y-1" data-testid="import-errors">
                  <p className="text-xs font-medium text-amber-700">{importResult.errors.length} warning{importResult.errors.length > 1 ? 's' : ''} during import:</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {importResult.errors.map((err, i) => (
                      <div key={i} className="text-xs text-amber-600">
                        <span className="font-mono">{err.invoice}</span>: {err.detail}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status breakdown of imported */}
              {importResult.status_breakdown && Object.keys(importResult.status_breakdown).length > 0 && (
                <div className="space-y-2" data-testid="result-status-breakdown">
                  <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Imported by Status</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(importResult.status_breakdown).map(([status, count]) => (
                      <span key={status} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-neutral-100 text-neutral-600'}`}>
                        {status.replace('_', ' ')} <span className="font-bold">{count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="border-t pt-3">
          {step === 'upload' && (
            <Button variant="ghost" onClick={handleClose} data-testid="cancel-btn">Cancel</Button>
          )}
          {step === 'preview' && (
            <div className="flex items-center justify-between w-full">
              <Button variant="ghost" size="sm" onClick={reset} data-testid="back-btn">
                <X className="w-4 h-4 mr-1" /> Start Over
              </Button>
              <div className="flex items-center gap-3">
                <span className="text-sm text-neutral-500">{summary.will_import || 0} invoices ready</span>
                <Button
                  onClick={handleExecuteImport}
                  disabled={!summary.will_import}
                  data-testid="execute-import-btn"
                  className="bg-gray-900 hover:bg-gray-800"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Import {summary.will_import || 0} Invoices
                </Button>
              </div>
            </div>
          )}
          {step === 'done' && (
            <Button onClick={handleClose} data-testid="close-done-btn" className="bg-gray-900 hover:bg-gray-800">
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCard({ label, value, icon, color = 'gray', testId, isText = false }) {
  const colors = {
    teal: 'bg-teal-50 text-teal-700 border-teal-100',
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    gray: 'bg-white/40 text-neutral-600 border-white/30',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[color] || colors.gray}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <span className="text-[10px] uppercase tracking-wide font-medium opacity-70">{label}</span>
      </div>
      <p className={`font-bold ${isText ? 'text-sm' : 'text-xl'}`}>{value}</p>
    </div>
  );
}
